import { PureComponent, ComponentType, PropsWithChildren } from 'react';

import { ConnectHost } from '@atlassian/bitbucket-connect-js';
import { ConnectIframe, AppOptions } from '@atlassian/connect-module-core';

import Connect from '../../main/connect';
import { isNumeric } from '../../main/utils';

import IframeFailedIndicator from './iframeFailedIndicator';
import IframeLoadingIndicator from './iframeLoadingIndicator';
import IframeTimeoutIndicator, {
  LoadingTimeoutComponent,
} from './iframeTimeoutIndicator';
import ConnectIframeProvider, { ConnectIframeProviderProps } from './provider';
import {
  iframeEmptyContainer,
  IframeContainer,
  IframeContainerProps,
} from './utils';

type IframeOptions = Partial<
  AppOptions & {
    isFullPage: boolean;
    sandbox: string;
    noSub: boolean;
    // not offically supported - can be used to pass FF's to internal apps (e.g Pipelines)
    __featuresInternal: { [key: string]: boolean };
  }
>;

export type IframeProps = {
  width?: number | string;
  height?: number | string;
  options?: IframeOptions;
  onHideInlineDialog?: Function;
  loadingComponent?: ComponentType<PropsWithChildren<any>>;
  loadingFailedComponent?: ComponentType<PropsWithChildren<any>>;
  loadingTimeoutComponent?: LoadingTimeoutComponent;
  iframeContainer?: IframeContainer;
  publishUiEvent?: (event: {}, callback?: () => void) => void;
  captureMessageWithTags?: (message: string, tags?: {}, extras?: {}) => void;
} & ConnectIframeProviderProps;

type UrlIframePropsOwnProps = {
  moduleId: string;
  moduleKey: string;
  appKey: string;
  url: string;
  moduleOptions?: any;
};

export type UrlIframeProps = UrlIframePropsOwnProps & IframeProps;

export class UrlIframe extends PureComponent<
  UrlIframeProps & { connectHost: typeof ConnectHost }
> {
  /* eslint-disable @typescript-eslint/no-empty-function */
  static options: AppOptions = {
    autoresize: false,
    widthinpx: false,
    hostFrameOffset: 0,
    _contextualOperations: {
      resize(): void {},
      sizeToParent(): void {},
      hideInlineDialog(): void {},
    },
  };
  /* eslint-enable @typescript-eslint/no-empty-function */
  static IFRAME_SANDBOX =
    'allow-forms allow-modals allow-popups allow-scripts allow-same-origin allow-top-navigation-by-user-activation allow-downloads';

  render() {
    const {
      moduleId,
      appKey,
      moduleKey,
      url,
      connectHost,
      width,
      height,
      options: defaultOptions,
      moduleOptions,
      loadingComponent: LoadingIndicator = IframeLoadingIndicator,
      loadingFailedComponent: FailedIndicator = IframeFailedIndicator,
      loadingTimeoutComponent: TimeoutIndicator = IframeTimeoutIndicator,
      iframeContainer = iframeEmptyContainer,
      // provider props
      loadingTimeout,
      defaultStyles,
      handleHideInlineDialog,
      handleIframeLoadingStarted,
      handleIframeLoadingComplete,
      handleIframeUnload,
      buildIframeStyles,
    } = this.props;

    const options: IframeOptions = {
      ...defaultOptions,
      ...moduleOptions,
      __featuresInternal: {
        // eslint-disable-next-line no-underscore-dangle
        ...defaultOptions?.__featuresInternal,
      },
      sandbox: UrlIframe.IFRAME_SANDBOX,
      noSub: true,
    };

    const connectIframeProvider = new ConnectIframeProvider({
      loadingTimeout,
      defaultStyles,
      handleHideInlineDialog,
      handleIframeLoadingStarted,
      handleIframeLoadingComplete,
      handleIframeUnload,
      buildIframeStyles,
    });

    return (
      <ConnectIframe
        key={moduleId}
        connectHost={connectHost}
        appKey={appKey}
        moduleKey={moduleKey}
        // casting to PureComponent as strict requirement not needed
        loadingIndicator={LoadingIndicator as typeof PureComponent}
        // casting to PureComponent as strict requirement not needed
        failedToLoadIndicator={FailedIndicator as typeof PureComponent}
        // casting to PureComponent as strict requirement not needed
        timeoutIndicator={
          ((timeoutIndicatorProps: any) => (
            <TimeoutIndicator
              {...timeoutIndicatorProps}
              publishUiEvent={this.props.publishUiEvent}
              captureMessageWithTags={this.props.captureMessageWithTags}
              context={{
                url,
                ...(options as any).productContext,
              }}
            />
          )) as unknown as new () => PureComponent<{
            failedCallback: Function;
          }>
        }
        url={url}
        options={{ ...UrlIframe.options, ...options }}
        width={isNumeric(width) ? String(width) : ''}
        height={isNumeric(height) ? String(height) : ''}
        connectIframeProvider={connectIframeProvider}
        // casting to PureComponent as strict requirement not needed
        iframeContainer={
          iframeContainer as new () => PureComponent<IframeContainerProps>
        }
      />
    );
  }
}

// eslint-disable-next-line import/no-default-export
export default Connect(UrlIframe);
